import { useState } from "react"
import { useNavigate, Link } from "@tanstack/react-router"
import { authClient } from "../lib/auth-client"
import { Button, Input, Label } from "../components/ui"
import { GoogleIcon } from "../components/icons"

/** Login page. Supports email/password and Google OAuth. Redirects to the dashboard if already signed in. */
export default function LoginPage() {
  const navigate = useNavigate()
  const { data: session } = authClient.useSession()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  if (session) {
    navigate({ to: "/" })
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    const res = await authClient.signIn.email({ email, password })
    setLoading(false)
    if (res.error) setError(res.error.message || "Login failed")
    else navigate({ to: "/" })
  }

  const signInWithGoogle = async () => {
    setError("")
    setGoogleLoading(true)
    const res = await authClient.signIn.social({ provider: "google", callbackURL: "/" })
    setGoogleLoading(false)
    if (res.error) setError(res.error.message || "Google login failed")
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-xl">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="flex flex-col items-center gap-3">
            <Link to="/landing">
              <img src="/logo.svg" alt="Taskoru" className="h-14 w-14 rounded-2xl shadow-md" />
            </Link>
            <h1 className="text-2xl font-bold tracking-tight">Login to Taskoru</h1>
            <p className="text-sm text-muted-foreground">Welcome back. Please sign in to continue.</p>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div>
            <Label>Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <Button type="submit" className="w-full" isLoading={loading}>Login</Button>
          <div className="relative flex items-center justify-center py-1">
            <span className="absolute inset-x-0 top-1/2 h-px bg-border" />
            <span className="relative bg-card px-2 text-xs text-muted-foreground">or</span>
          </div>
          <Button type="button" variant="outline" className="w-full" onClick={signInWithGoogle} isLoading={googleLoading}>
            <GoogleIcon className="mr-2 h-4 w-4" /> Continue with Google
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Don't have an account? <Link to="/register" className="font-medium text-primary hover:underline">Register</Link>
          </p>
          <p className="text-center text-sm text-muted-foreground">
            Want to learn more? <Link to="/landing" className="font-medium text-primary hover:underline">Visit landing page</Link>
          </p>
        </form>
      </div>
    </div>
  )
}
